import React, { useState } from 'react';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FormularioFieldPalette } from './FormularioFieldPalette';
import { FormularioFieldConfig } from './FormularioFieldConfig';
import { FormularioFieldItem } from './FormularioFieldItem';
import { FormularioPreview } from './FormularioPreview';
import { FormularioAreasManager } from './FormularioAreasManager';
import { useFormularioCampos } from '@/hooks/useFormularioCampos';
import { useFormularioAreas } from '@/hooks/useFormularioAreas';
import { useFormularioBuilder } from '@/hooks/useFormularioBuilder';
import { FormularioCampo } from '@/types/formulario';
import { Save, ArrowLeft, Layers } from 'lucide-react';
import { toast } from 'sonner';

interface FormularioBuilderProps {
  formularioId: string;
  formularioTitulo: string;
  onClose: () => void;
}

export const FormularioBuilder = ({ formularioId, formularioTitulo, onClose }: FormularioBuilderProps) => {
  const { campos, addCampo, updateCampo, deleteCampo, reorderCampos, isLoading } = useFormularioCampos(formularioId);
  const { areas } = useFormularioAreas(formularioId);
  const { selectedCampoId, selectCampo, clearSelection } = useFormularioBuilder();
  const [areasManagerOpen, setAreasManagerOpen] = useState(false);

  const handleAddField = (tipo: FormularioCampo['tipo_campo']) => {
    const ordem = campos.length;
    const novoCampo: Omit<FormularioCampo, 'id' | 'criado_em' | 'atualizado_em'> = {
      formulario_id: formularioId,
      tipo_campo: tipo,
      label: `Novo Campo ${tipo}`,
      obrigatorio: false,
      ordem,
      ativo: true,
      configuracoes: tipo === 'select' || tipo === 'radio' || tipo === 'checkbox' 
        ? { opcoes: ['Opção 1', 'Opção 2'] } 
        : {},
    };
    addCampo(novoCampo);
  };

  const handleUpdateCampo = (updates: Partial<FormularioCampo>) => {
    if (!selectedCampoId) return;
    updateCampo({ id: selectedCampoId, ...updates });
  };

  const handleDeleteCampo = (campoId: string) => {
    deleteCampo(campoId);
    if (selectedCampoId === campoId) {
      clearSelection();
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = campos.findIndex((c) => c.id === active.id);
      const newIndex = campos.findIndex((c) => c.id === over.id);

      const reordered = arrayMove(campos, oldIndex, newIndex);
      const updates = reordered.map((campo, index) => ({
        id: campo.id,
        ordem: index,
      }));

      reorderCampos(updates);
    }
  };

  const selectedCampo = campos.find(c => c.id === selectedCampoId) || null;

  // Agrupar campos por área
  const camposPorArea = React.useMemo(() => {
    const grupos: Record<string, FormularioCampo[]> = {
      'sem-area': [],
    };

    areas.forEach(area => {
      grupos[area.id] = [];
    });

    campos.forEach(campo => {
      if (campo.area_id && grupos[campo.area_id]) {
        grupos[campo.area_id].push(campo);
      } else {
        grupos['sem-area'].push(campo);
      }
    });

    return grupos;
  }, [campos, areas]);

  if (isLoading) {
    return <div className="flex items-center justify-center h-full">Carregando...</div>;
  }

  if (areasManagerOpen) {
    return <FormularioAreasManager formularioId={formularioId} onClose={() => setAreasManagerOpen(false)} />;
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b p-4 flex items-center justify-between bg-background">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onClose}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold">{formularioTitulo}</h2>
            <p className="text-sm text-muted-foreground">Editor de Formulário</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAreasManagerOpen(true)}>
            <Layers className="h-4 w-4 mr-2" />
            Gerenciar Áreas
          </Button>
          <Button onClick={() => toast.success('Formulário salvo automaticamente')}>
            <Save className="h-4 w-4 mr-2" />
            Salvar
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <div className="grid grid-cols-12 gap-4 h-full p-4">
          {/* Lado Esquerdo: Editor */}
          <div className="col-span-7 overflow-y-auto space-y-4">
            {/* Paleta de Campos */}
            <FormularioFieldPalette onAddField={handleAddField} />

            {/* Lista de Campos */}
            <Card className="p-4">
              <h3 className="text-lg font-semibold mb-4">Campos do Formulário</h3>
              {campos.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Nenhum campo adicionado. Selecione um tipo de campo na paleta.
                </p>
              ) : (
                <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={campos.map(c => c.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-4">
                      {/* Áreas */}
                      {areas.map((area) => (
                        <div key={area.id} className="space-y-2">
                          <div className="flex items-center gap-2 px-2 py-1 bg-primary/10 rounded">
                            <Layers className="h-4 w-4 text-primary" />
                            <h4 className="font-semibold text-sm text-primary">{area.titulo}</h4>
                            <span className="text-xs text-muted-foreground">
                              ({camposPorArea[area.id]?.length || 0} campos)
                            </span>
                          </div>
                          {camposPorArea[area.id]?.map((campo) => (
                            <FormularioFieldItem
                              key={campo.id}
                              campo={campo}
                              isSelected={selectedCampoId === campo.id}
                              onSelect={() => selectCampo(campo.id)}
                              onDelete={() => handleDeleteCampo(campo.id)}
                            />
                          ))}
                        </div>
                      ))}

                      {/* Campos sem área */}
                      {camposPorArea['sem-area'].length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 px-2 py-1 bg-muted rounded">
                            <h4 className="font-semibold text-sm text-muted-foreground">Sem Área</h4>
                            <span className="text-xs text-muted-foreground">
                              ({camposPorArea['sem-area'].length} campos)
                            </span>
                          </div>
                          {camposPorArea['sem-area'].map((campo) => (
                            <FormularioFieldItem
                              key={campo.id}
                              campo={campo}
                              isSelected={selectedCampoId === campo.id}
                              onSelect={() => selectCampo(campo.id)}
                              onDelete={() => handleDeleteCampo(campo.id)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </Card>

            {/* Configuração do Campo Selecionado */}
            {selectedCampo && (
              <FormularioFieldConfig
                campo={selectedCampo}
                areas={areas}
                onUpdate={handleUpdateCampo}
                onClose={clearSelection}
              />
            )}
          </div>

          {/* Lado Direito: Preview em Tempo Real */}
          <div className="col-span-5 overflow-y-auto">
            <div className="sticky top-0">
              <FormularioPreview campos={campos} formularioId={formularioId} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
